import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import {
  db, doc, updateDoc, collection, query, where, getDocs,
  storage, ref, uploadBytes, getDownloadURL,
} from '../firebase/config';
import { useLocationLabel } from '../hooks/useLocationLabel';
import {
  Check, ArrowRight, ChevronLeft,
  Briefcase, Camera, X, MapPin,
  Navigation, Loader2, Building2, Search,
} from 'lucide-react';

const MAX_CATEGORIES = 3;
const MIN_SERVICES = 3;

// ---------- Nominatim Search Helper ----------
const searchAddress = async (query) => {
  if (!query || query.length < 3) return [];
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'Skytone/1.0 (contact@skytoneby.co.za)'
        }
      }
    );
    return await response.json();
  } catch (error) {
    console.error('Address search failed:', error);
    return [];
  }
};

// ---------- Image compression ----------
const compressImage = (file, maxDim = 1024, quality = 0.85) =>
  new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) return resolve(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width >= height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return resolve(file);
            const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
            resolve(new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() }));
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const OnboardingProfessional = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const {
    currentLocation,
    requestLocation,
    loading: locationLoading,
  } = useLocation();

  const [step, setStep] = useState(1);
  const [location, setLocation] = useState('');
  const [unitNumber, setUnitNumber] = useState('');
  const [coordinates, setCoordinates] = useState(currentLocation || null);
  const [specializations, setSpecializations] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [geoError, setGeoError] = useState(null);
  const fileInputRef = useRef(null);

  // Address Search State
  const [addressQuery, setAddressQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimerRef = useRef(null);

  const businessName = userData?.businessName || userData?.name || '';
  const locationLabel = useLocationLabel(coordinates);

  useEffect(() => {
    if (locationLabel && !location && !addressQuery) {
      setLocation(locationLabel);
      setAddressQuery(locationLabel);
    }
  }, [locationLabel, location, addressQuery]);

  const categories = [
    { id: 'hairstyling', name: 'Hair', image: 'woman-hair.svg' },
    { id: 'nailartist', name: 'Nails', image: 'nail-polish.svg' },
    { id: 'lashtech', name: 'Lashes', image: 'mascara.svg' },
    { id: 'esthetician', name: 'Skin', image: 'make-up.svg' },
  ];

  const serviceOptions = {
    hairstyling: [
      "Women's Haircut", "Men's Haircut", 'Coloring', 'Blow-dry',
      'Highlights', 'Balayage', 'Supplier',
    ],
    nailartist: [
      'Manicure', 'Pedicure', 'Gel Nails', 'Acrylic Nails',
      'Dip Powder', 'Nail Art', 'Supplier',
    ],
    lashtech: [
      'Classic Lashes', 'Hybrid Lashes', 'Volume Lashes',
      'Mega Volume', 'Lash Lift', 'Supplier',
    ],
    esthetician: [
      'Facial', 'Chemical Peel', 'Microdermabrasion',
      'Extractions', 'LED Therapy', 'Supplier',
    ],
  };

  // ---------- Image upload (Storage, compressed) ----------
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file.');
      return;
    }

    // instant local preview so the UI feels responsive
    const previewUrl = URL.createObjectURL(file);
    setProfileImage(previewUrl);
    setUploadingImage(true);

    try {
      const compressed = await compressImage(file, 1024, 0.85);
      const path = `avatars/${userData.uid}/${Date.now()}_${compressed.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, compressed, { contentType: 'image/jpeg' });
      const downloadUrl = await getDownloadURL(storageRef);

      // Replace preview with the real URL that will be saved
      URL.revokeObjectURL(previewUrl);
      setProfileImage(downloadUrl);
    } catch (err) {
      console.error('Image upload failed:', err);
      alert('Could not upload your photo. Please try again.');
      setProfileImage(null);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    setProfileImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toggleSpecialization = (id) => {
    setSpecializations((prev) => {
      if (prev.includes(id)) {
        const next = prev.filter((s) => s !== id);
        setSelectedServices((sel) =>
          sel.filter((s) => !(serviceOptions[id] || []).includes(s))
        );
        return next;
      }
      if (prev.length >= MAX_CATEGORIES) {
        alert(`You can pick up to ${MAX_CATEGORIES} specializations`);
        return prev;
      }
      return [...prev, id];
    });
  };

  const toggleService = (service) => {
    if (selectedServices.includes(service)) {
      setSelectedServices(selectedServices.filter((s) => s !== service));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  const handleUseCurrentLocation = async () => {
    setGeoError(null);
    try {
      const result = await requestLocation();
      const resolved = result || currentLocation;
      if (resolved?.lat != null && resolved?.lng != null) {
        setCoordinates(resolved);
      } else {
        setGeoError('Could not read your location. Try again or type it manually.');
      }
    } catch (err) {
      console.error('Geolocation failed:', err);
      setGeoError('Location permission denied. You can type your address instead.');
    }
  };

  const isBusinessNameAvailable = async (name) => {
    if (!name || !name.trim()) return true;
    try {
      const q = query(
        collection(db, 'users'),
        where('businessName', '==', name.trim()),
        where('role', '==', 'professional')
      );
      const snap = await getDocs(q);
      return !snap.docs.some(d => d.id !== userData.uid);
    } catch (err) {
      console.error('Name check failed:', err);
      return true;
    }
  };

  const completeOnboarding = async () => {
    if (selectedServices.length < MIN_SERVICES) {
      alert(`Please select at least ${MIN_SERVICES} services`);
      return;
    }
    if (specializations.length === 0) {
      alert('Please pick at least one specialization');
      return;
    }
    if (uploadingImage) {
      alert('Please wait for your photo to finish uploading.');
      return;
    }

    if (businessName) {
      const available = await isBusinessNameAvailable(businessName);
      if (!available) {
        alert(
          `The business name "${businessName}" is already taken. ` +
          `Please sign up again with a different name, or contact support.`
        );
        return;
      }
    }

    setLoading(true);
    try {
      const primarySpecialization = specializations[0];

      const payload = {
        location: location,
        unitNumber: unitNumber.trim() || null,
        coordinates: coordinates
          ? { ...coordinates, label: locationLabel || coordinates.label || null }
          : null,
        specialization: primarySpecialization,
        specializations: specializations,
        services: selectedServices,
        profileImage: profileImage || null,
        onboardingComplete: true,
        userType: 'professional',
        role: 'professional',
        updatedAt: new Date().toISOString(),
      };

      // Only set businessName if it's missing from the signup doc
      if (!userData?.businessName && businessName) {
        payload.businessName = businessName.trim();
        payload.businessNameLower = businessName.trim().toLowerCase();
      }

      console.log('[Onboarding] saving payload:', payload);
      await updateDoc(doc(db, 'users', userData.uid), payload);
      console.log('[Onboarding] saved OK');
      navigate('/professional-dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      alert(
        `Failed to complete onboarding: ${error.code || ''} ${error.message || ''}`
      );
    } finally {
      setLoading(false);
    }
  };

  const totalSteps = 4;
  const canProceedStep2 = specializations.length > 0;
  const canProceedStep3 = selectedServices.length >= MIN_SERVICES;
  const canProceedStep1 = !!location && !!coordinates;

  const servicesByCategory = specializations.map((id) => ({
    id,
    label: categories.find((c) => c.id === id)?.name || id,
    services: serviceOptions[id] || [],
  }));

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-4 py-8 relative overflow-hidden bg-[#8B5FBF]"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 50% 20%, rgba(205,188,231,0.35), rgba(107,74,150,0.55) 70%)',
        }}
      />

      <div className="relative w-full max-w-md z-10">
        <div className="backdrop-blur-2xl bg-white/25 border border-white/40 rounded-[2rem] shadow-2xl overflow-hidden p-6 max-h-[92vh] overflow-y-auto">

          {/* Progress Steps */}
          <div className="flex gap-1 mb-6">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`flex-1 h-1 rounded-full transition-colors ${
                  i + 1 <= step ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>

          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              className="mb-4 text-white/70 hover:text-white transition-colors flex items-center gap-1 text-sm"
            >
              <ChevronLeft size={16} /> Back
            </button>
          )}

          {/* ============ STEP 1 — STUDIO SETUP ============ */}
          {step === 1 && (
            <div className="animate-fade-in">
              <div className="bg-white/95 rounded-3xl p-6 shadow-lg">
                <h1 className="text-3xl font-serif font-bold text-gray-900 mb-2 leading-tight">
                  Set up your studio.
                </h1>
                <p className="text-gray-500 mb-6 text-sm">
                  {businessName ? (
                    <>
                      Setting up <span className="font-bold text-gray-700">{businessName}</span>
                    </>
                  ) : (
                    'Let clients know who you are.'
                  )}
                </p>

                <div className="space-y-6">
                  {/* Image Upload */}
                  <div>
                    <label className="text-[11px] font-bold text-gray-900 uppercase tracking-widest ml-1 mb-1.5 block">
                      Profile Photo
                    </label>
                    <div className="flex flex-col items-center">
                      {profileImage ? (
                        <div className="relative">
                          <img
                            src={profileImage}
                            alt="Profile"
                            className="w-24 h-24 rounded-full object-cover border-2 border-[#CDBCE7]"
                          />
                          {uploadingImage && (
                            <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center">
                              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            </div>
                          )}
                          <button
                            onClick={removeImage}
                            disabled={uploadingImage}
                            className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="w-24 h-24 bg-[#FCF8F9] border-2 border-dashed border-[#CDBCE7] rounded-full flex flex-col items-center justify-center cursor-pointer hover:bg-[#F4EEFF] transition-colors"
                        >
                          <Camera size={24} className="text-[#CDBCE7] mb-1" />
                          <span className="text-[9px] font-bold text-[#CDBCE7] uppercase tracking-widest">
                            Upload
                          </span>
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </div>
                    {uploadingImage && (
                      <p className="text-[10px] text-center text-gray-400 mt-2">
                        Uploading…
                      </p>
                    )}
                  </div>

                  {/* Studio Address with Autocomplete */}
                  <div>
                    <label className="text-[11px] font-bold text-gray-900 uppercase tracking-widest ml-1 mb-1.5 block">
                      Studio Address
                    </label>

                    <div className="relative mb-2">
                      <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={addressQuery}
                        onChange={(e) => {
                          setAddressQuery(e.target.value);
                          setLocation(e.target.value);
                          if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

                          if (e.target.value.length >= 3) {
                            setSearching(true);
                            setShowSuggestions(true);
                            searchTimerRef.current = setTimeout(async () => {
                              const results = await searchAddress(e.target.value);
                              setSuggestions(results);
                              setSearching(false);
                            }, 600);
                          } else {
                            setSuggestions([]);
                            setShowSuggestions(false);
                            setSearching(false);
                          }
                        }}
                        onFocus={() => {
                          if (suggestions.length > 0) setShowSuggestions(true);
                        }}
                        className="w-full bg-[#FCF8F9] border border-gray-100 rounded-2xl pl-11 pr-10 py-4 text-sm font-medium text-gray-900 focus:outline-none focus:border-[#CDBCE7] focus:ring-2 focus:ring-[#CDBCE7]/20 transition-all placeholder:text-gray-400"
                        placeholder="Search address (e.g. 12 Oxford Rd)"
                        required
                      />
                      {searching && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          <div className="w-4 h-4 border-2 border-[#8B5FBF] border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      )}
                    </div>

                    {showSuggestions && suggestions.length > 0 && (
                      <div className="absolute left-6 right-6 mt-1 bg-white border border-gray-100 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto">
                        {suggestions.map((place) => (
                          <button
                            key={place.place_id}
                            type="button"
                            onClick={() => {
                              setAddressQuery(place.display_name);
                              setLocation(place.display_name);
                              setCoordinates({
                                lat: parseFloat(place.lat),
                                lng: parseFloat(place.lon),
                                label: place.display_name,
                              });
                              setShowSuggestions(false);
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-[#FCF8F9] border-b border-gray-50 last:border-0 text-sm text-gray-700"
                          >
                            {place.display_name}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="mt-2 relative">
                      <Building2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={unitNumber}
                        onChange={(e) => setUnitNumber(e.target.value)}
                        className="w-full bg-[#FCF8F9] border border-gray-100 rounded-2xl pl-11 pr-5 py-4 text-sm font-medium text-gray-900 focus:outline-none focus:border-[#CDBCE7] focus:ring-2 focus:ring-[#CDBCE7]/20 transition-all placeholder:text-gray-400"
                        placeholder="Unit / Shop number (optional)"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleUseCurrentLocation}
                      disabled={locationLoading}
                      className={`mt-2 w-full py-3 rounded-2xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                        coordinates
                          ? 'bg-[#F4EEFF] text-[#8B5FBF]'
                          : 'bg-white border-2 border-[#CDBCE7]/40 text-[#8B5FBF] hover:bg-[#F4EEFF]'
                      } disabled:opacity-50`}
                    >
                      {locationLoading ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          Getting location…
                        </>
                      ) : coordinates ? (
                        <>
                          <Check size={14} />
                          Location pinned
                          {locationLabel && (
                            <span className="ml-1 font-normal opacity-70">· {locationLabel}</span>
                          )}
                        </>
                      ) : (
                        <>
                          <Navigation size={14} />
                          Pin my current location
                        </>
                      )}
                    </button>

                    {geoError && (
                      <p className="text-[11px] text-red-500 mt-2 px-1">{geoError}</p>
                    )}

                    <p className="text-[10px] text-gray-400 mt-2 px-1 leading-relaxed">
                      Clients use this to find you and get directions. Use the{' '}
                      <span className="font-bold text-gray-600">search bar</span> to get pinned exactly on the map.
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => canProceedStep1 && setStep(2)}
                  disabled={!canProceedStep1}
                  className={`w-full mt-8 py-4 rounded-full font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    canProceedStep1
                      ? 'bg-[#010101] text-white shadow-xl hover:bg-black/90 active:scale-[0.98]'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Continue <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {/* ============ STEP 2 — CATEGORIES ============ */}
          {step === 2 && (
            <div className="animate-fade-in">
              <div className="bg-white/95 rounded-3xl p-6 shadow-lg">
                <h1 className="text-3xl font-serif font-bold text-gray-900 mb-2 leading-tight">
                  What do you do?
                </h1>
                <p className="text-gray-500 mb-6 text-sm">
                  Pick up to {MAX_CATEGORIES} specializations — {specializations.length}/
                  {MAX_CATEGORIES} selected
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  {categories.map((cat) => {
                    const selected = specializations.includes(cat.id);
                    const atLimit = specializations.length >= MAX_CATEGORIES && !selected;
                    return (
                      <div
                        key={cat.id}
                        onClick={() => toggleSpecialization(cat.id)}
                        className={`relative p-4 rounded-[2rem] border-2 transition-all flex flex-col items-center gap-3 cursor-pointer ${
                          selected
                            ? 'border-[#8B5FBF] bg-[#F4EEFF] shadow-sm'
                            : atLimit
                            ? 'border-gray-50 bg-white opacity-40 cursor-not-allowed'
                            : 'border-gray-50 bg-white hover:border-[#CDBCE7]/50'
                        }`}
                      >
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className={`w-16 h-16 object-contain transition-opacity duration-200 ${
                            selected ? 'opacity-100' : 'opacity-40'
                          }`}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span className={`text-sm font-bold text-center ${selected ? 'text-gray-900' : 'text-gray-500'}`}>
                          {cat.name}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={() => canProceedStep2 && setStep(3)}
                  disabled={!canProceedStep2}
                  className={`w-full py-4 rounded-full font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    canProceedStep2
                      ? 'bg-[#010101] text-white shadow-xl hover:bg-black/90 active:scale-[0.98]'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Continue <ArrowRight size={18} />
                </button>

                {specializations.length === 0 && (
                  <p className="text-center text-xs text-gray-400 mt-3">
                    Pick at least one to continue
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ============ STEP 3 — SERVICES ============ */}
          {step === 3 && (
            <div className="animate-fade-in">
              <div className="bg-white/95 rounded-3xl p-6 shadow-lg">
                <h1 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                  Select your services
                </h1>
                <p className="text-gray-500 mb-6 text-sm">
                  Choose at least {MIN_SERVICES} services across your specialties
                </p>

                <div className="space-y-5 mb-8 max-h-[55vh] overflow-y-auto pr-1 hide-scrollbar">
                  {servicesByCategory.map((group) => (
                    <div key={group.id}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                          {group.label}
                        </span>
                        <span className="flex-1 h-px bg-gray-100"></span>
                      </div>
                      <div className="space-y-2">
                        {group.services.map((service) => {
                          const isSelected = selectedServices.includes(service);
                          return (
                            <button
                              key={service}
                              onClick={() => toggleService(service)}
                              className={`w-full p-4 rounded-2xl border-2 transition-all flex items-center justify-between ${
                                isSelected
                                  ? 'border-[#8B5FBF] bg-[#F4EEFF]'
                                  : 'border-gray-100 bg-white hover:border-[#CDBCE7]/50'
                              }`}
                            >
                              <span className={`text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>
                                {service}
                              </span>
                              {isSelected && (
                                <div className="w-6 h-6 rounded-full bg-[#8B5FBF] flex items-center justify-center shrink-0">
                                  <Check size={14} className="text-white" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => canProceedStep3 && setStep(4)}
                  disabled={!canProceedStep3}
                  className={`w-full py-4 rounded-full font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                    canProceedStep3
                      ? 'bg-[#010101] text-white shadow-xl hover:bg-black/90 active:scale-[0.98]'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Continue <ArrowRight size={18} />
                </button>
                <p className="text-center text-xs text-gray-400 mt-3">
                  Select {Math.max(0, MIN_SERVICES - selectedServices.length)} more{' '}
                  {selectedServices.length < MIN_SERVICES ? 'service' : 'services'}
                </p>
              </div>
            </div>
          )}

          {/* ============ STEP 4 — CONFIRM ============ */}
          {step === 4 && (
            <div className="animate-fade-in">
              <div className="bg-white/95 rounded-3xl p-6 shadow-lg text-center">
                <div className="w-24 h-24 bg-[#FCF8F9] rounded-full flex items-center justify-center mx-auto mb-6 overflow-hidden">
                  {profileImage ? (
                    <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-16 h-16 bg-[#CDBCE7] rounded-full flex items-center justify-center">
                      <Check size={32} className="text-white" />
                    </div>
                  )}
                </div>
                <h1 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                  Ready to grow!
                </h1>
                <p className="text-gray-400 mb-8 text-sm">
                  Start accepting bookings and growing your beauty business
                </p>

                <div className="bg-[#FCF8F9] rounded-2xl p-4 mb-8 text-left">
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase size={16} className="text-[#CDBCE7]" />
                    <span className="text-sm font-bold text-gray-700">
                      {businessName || 'Your Business'}
                    </span>
                  </div>

                  {specializations.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {specializations.map((id) => {
                        const cat = categories.find((c) => c.id === id);
                        return (
                          <span
                            key={id}
                            className="bg-[#F4EEFF] text-[#8B5FBF] px-2.5 py-1 rounded-full text-[11px] font-bold"
                          >
                            {cat?.name || id}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                    {selectedServices.slice(0, 4).map((service) => (
                      <span
                        key={service}
                        className="bg-white px-3 py-1 rounded-full text-xs font-medium text-gray-600 border border-gray-100"
                      >
                        {service}
                      </span>
                    ))}
                    {selectedServices.length > 4 && (
                      <span className="bg-white px-3 py-1 rounded-full text-xs font-medium text-gray-400 border border-gray-100">
                        +{selectedServices.length - 4} more
                      </span>
                    )}
                  </div>

                  {location && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-start gap-1.5 text-xs text-gray-500">
                      <MapPin size={12} className="text-[#CDBCE7] shrink-0 mt-0.5" />
                      <span className="break-words">
                        {coordinates && <span className="font-bold">Pinned · </span>}
                        {location}
                        {unitNumber && <span> · {unitNumber}</span>}
                      </span>
                    </div>
                  )}
                </div>

                <button
                  onClick={completeOnboarding}
                  disabled={loading || uploadingImage}
                  className="w-full bg-[#010101] text-white py-4 rounded-full font-bold text-sm shadow-xl hover:bg-black/90 transition-colors active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Setting up...
                    </div>
                  ) : uploadingImage ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Uploading photo...
                    </div>
                  ) : (
                    <>
                      Go to Dashboard <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.4s ease-out forwards; }
      `,
        }}
      />
    </div>
  );
};

export default OnboardingProfessional;